export const portfolioData = {
  personal: {
    name: "Ranjithkumar Sekar",
    headline: "Application Engineer | Automation & AI",
    subheadline: "Building AI-Powered Infrastructure & Intelligent Automation Systems",
    description: "I design and build scalable platforms, automation frameworks, AI-powered workflows, enterprise integrations, and infrastructure that help organizations operate faster, smarter, and more efficiently.",
    email: "ranjithkumar98.sekar@gmail.com",
    phone: "+91 9750580343",
    location: "Namakkal, Tamil Nadu, India",
    github: "https://github.com/ranjitkumar",
    linkedin: "https://www.linkedin.com/in/ranjithkumar-s/",
    resumeUrl: "/resume.pdf",
    avatar: "https://avatars.githubusercontent.com/u/ranjit3770"
  },

  metrics: {
    yearsExperience: "3+",
    enterpriseProjects: "20+",
    serversManaged: "12+",
    apiTransactions: "100K+",
    aiSystems: "5+",
    enterprisePlatforms: "10+",
    automationWorkflows: "Multiple",
    infraDeployments: "Multiple"
  },

  metricsDashboard: [
    { label: "Projects Delivered", value: "20+", color: "from-indigo-500 to-blue-600" },
    { label: "Production Servers Managed", value: "12+", color: "from-purple-500 to-pink-600" },
    { label: "AI Systems Built", value: "5+", color: "from-violet-500 to-purple-600" },
    { label: "Enterprise Platforms", value: "10+", color: "from-cyan-500 to-teal-600" },
    { label: "API Transactions", value: "100K+", color: "from-amber-500 to-orange-600" },
    { label: "Automation Workflows", value: "Multiple", color: "from-emerald-500 to-green-600" }
  ],

  heroArchitecture: [
    { id: "users", label: "Users", icon: "user", level: 0 },
    { id: "applications", label: "Applications", icon: "layout", level: 1 },
    { id: "api", label: "API Layer", icon: "shuffle", level: 2 },
    { id: "ai", label: "AI Agents", icon: "brain", level: 3 },
    { id: "infra", label: "Infrastructure", icon: "server", level: 4 },
    { id: "clusters", label: "Clusters", icon: "cpu", level: 5 },
    { id: "data", label: "Data Layer", icon: "database", level: 6 }
  ],

  brandIntersection: [
    { name: "AI", description: "Agents, LLMs, RAG, Model Deployment, Fine-tuning", icon: "brain" },
    { name: "Automation", description: "Workflows, Orchestration, n8n, Infrastructure Automation", icon: "zap" },
    { name: "Infrastructure", description: "Linux, Docker, HPC, Proxmox, Clusters, Security", icon: "server" },
    { name: "Backend Systems", description: "Python, FastAPI, Django, REST APIs, Microservices", icon: "code" },
    { name: "Platform Engineering", description: "Architecture, Scalability, Deployment, Reliability", icon: "layers" },
    { name: "Enterprise Applications", description: "Evaluation Portals, Middleware, Automation Tools", icon: "globe" }
  ],

  brandDescription: "I work across the entire stack — from Linux servers and HPC clusters to APIs and backend systems to AI agents and intelligent automation. My focus is engineering impact and system thinking, not just listing technologies.",

  aiEra: {
    title: "Engineering for the AI Era",
    description: "The future belongs to engineers who can combine AI, Automation, Infrastructure, and Software Engineering to create systems where humans and AI collaborate efficiently.",
    areas: [
      "AI Agents",
      "Local LLM Infrastructure",
      "RAG Systems",
      "Workflow Automation",
      "Self-hosted AI",
      "Enterprise AI Integrations",
      "Agentic Systems",
      "Voice AI",
      "Distributed Computing",
      "AI Operations (AIOps)"
    ],
    core: [
      { name: "AI", desc: "Agents, LLMs, RAG, Fine-tuning" },
      { name: "Automation", desc: "Workflows, Orchestration, n8n" },
      { name: "Infrastructure", desc: "Servers, Clusters, Docker, HPC" },
      { name: "Software Engineering", desc: "APIs, Backend, Architecture" }
    ]
  },

  workImpact: {
    infrastructure: [
      "Built and managed production-grade Frappe environments across 12+ servers",
      "Implemented centralized SSL lifecycle management with auto-renewal",
      "Designed backup and disaster recovery workflows",
      "Implemented monitoring and infrastructure automation systems"
    ],
    aiAutomation: [
      "Built AI-powered support workflows using Ollama + n8n",
      "Implemented AI-assisted HPC support systems with RAG pipelines",
      "Created AI-enhanced enterprise applications",
      "Developed dataset generation pipelines for LLM fine-tuning"
    ],
    backendSystems: [
      "Built Answer Evaluation Portal serving 10K+ scripts with 500+ faculty",
      "Built SMS Middleware Platform handling 100K+ API requests",
      "Built Job Submission Portal and Lab Policy Management Platform",
      "Developed REST APIs for academic integrations and external systems"
    ],
    infrastructureEngineering: [
      "Canvas LMS Deployment and configuration",
      "Proxmox Infrastructure Management for 40+ VMs",
      "HPC Support Platform with SLURM cluster deployment",
      "Distributed Desktop Cluster Architecture"
    ],
    apiIntegration: [
      "JioCX Integration for enterprise communication",
      "SMS Gateway Integration via Twilio and Aisensy",
      "External Examination APIs and Webhook Systems",
      "Authentication Systems and Automation Pipelines"
    ]
  },

  capabilities: [
    {
      category: "AI Engineering",
      icon: "brain",
      color: "from-violet-500 to-purple-600",
      items: ["Ollama", "Llama", "Prompt Engineering", "Fine-Tuning Pipelines", "AI Agents", "LangGraph", "CrewAI", "RAG", "Model Deployment"]
    },
    {
      category: "Automation Engineering",
      icon: "zap",
      color: "from-amber-500 to-orange-600",
      items: ["n8n", "Python Automation", "Workflow Orchestration", "Selenium", "Infrastructure Automation", "Network Automation", "Server Automation"]
    },
    {
      category: "Platform Engineering",
      icon: "layers",
      color: "from-cyan-500 to-teal-600",
      items: ["Linux", "Docker", "Proxmox", "Nginx", "Redis", "Supervisor", "Systemd", "HAProxy"]
    },
    {
      category: "Backend Engineering",
      icon: "code",
      color: "from-blue-500 to-indigo-600",
      items: ["Python", "FastAPI", "Django", "Flask", "REST APIs", "Microservices", "Authentication", "Middleware"]
    },
    {
      category: "Data Engineering",
      icon: "database",
      color: "from-emerald-500 to-green-600",
      items: ["MySQL", "MariaDB", "SQL Optimization", "Data Pipelines", "Data Collection", "Data Processing"]
    },
    {
      category: "Distributed Systems",
      icon: "cpu",
      color: "from-rose-500 to-pink-600",
      items: ["HPC", "SLURM", "Cluster Computing", "Distributed Storage", "GPU Infrastructure", "Dask", "JupyterHub"]
    }
  ],

  timeline: [
    {
      period: "2022 – 2024",
      role: "System Engineer",
      description: "Started my professional journey managing Linux systems, servers, enterprise applications, infrastructure deployments, troubleshooting, production support, and platform operations.",
      highlights: [
        "Linux Administration",
        "Server Management",
        "Infrastructure Support",
        "Platform Deployment",
        "Production Troubleshooting",
        "Application Operations"
      ],
      icon: "monitor"
    },
    {
      period: "2024 – Present",
      role: "Application Engineer | Automation & AI",
      description: "Expanded responsibilities beyond infrastructure into automation, backend engineering, AI systems, enterprise integrations, HPC environments, workflow orchestration, and intelligent platform development.",
      highlights: [
        "AI Workflow Automation",
        "Enterprise Application Development",
        "Infrastructure Automation",
        "HPC & Cluster Computing",
        "Platform Engineering",
        "API Development",
        "Self-hosted AI Systems",
        "Workflow Orchestration"
      ],
      icon: "sparkles",
      current: true
    }
  ],

  currentPosition: {
    role: "Application Engineer | Automation & AI",
    focus: [
      "AI Agents",
      "Workflow Automation",
      "Platform Engineering",
      "HPC Automation",
      "Self-hosted AI",
      "Infrastructure Intelligence"
    ]
  },

  projects: {
    featured: [
      {
        id: 1,
        title: "Answer Evaluation Portal",
        tag: "Enterprise Software",
        category: "Enterprise Applications",
        year: "2025",
        status: "Production",
        overview: "A secure, digitized evaluation platform enabling parallel marking of 10K+ university answer scripts with real-time monitoring, role-based access, and automated workflow management.",
        problem: "Manual evaluation of thousands of answer scripts was slow, error-prone, and resource-intensive for university examination boards with 500+ faculty members.",
        architecture: "Faculty → Web Portal → Django Backend → MySQL → Secure Script Viewer → Evaluation Engine",
        solution: "Built a secure digitized platform enabling parallel evaluation, real-time HoD dashboards, and automated workflow management with role-based access control.",
        technologies: ["Python", "Django", "MySQL", "JavaScript", "HTML/CSS", "REST API", "Nginx"],
        features: [
          "Digital answer script viewer with watermarking and secure delivery",
          "Automated course, exam, and faculty management",
          "Bulk upload for question papers and answer scripts",
          "HoD dashboard with real-time evaluation progress",
          "Role-based authentication and audit trails",
          "Dynamic REST APIs for system integration"
        ],
        challenges: [
          "Secure delivery of answer scripts to 500+ remote evaluators",
          "Concurrent access and data integrity across evaluation lifecycle",
          "Building intuitive UI for non-technical faculty users"
        ],
        impact: "10,000+ scripts evaluated, 500+ faculty onboarded, 70% faster evaluation cycle",
        lessons: "Role-based access and comprehensive audit trails are critical for enterprise academic platforms handling sensitive evaluation data.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 2,
        title: "AI Agent Workflow Automation",
        tag: "AI & Automation",
        category: "AI",
        year: "2025",
        status: "Production",
        overview: "An AI-powered workflow automation system using Ollama LLMs with RAG pipelines, integrated with n8n for orchestrating complex enterprise automation workflows.",
        problem: "Manual policy management and support workflows required significant human effort, leading to slow response times and configuration inconsistencies across the organization.",
        architecture: "Users → AI Assistant → Ollama LLM → RAG Pipeline → n8n Workflow → Enterprise Systems",
        solution: "Created an AI assistant with natural language understanding, RAG over domain documentation, and n8n-powered workflow orchestration for automated policy deployment and support.",
        technologies: ["Ollama", "n8n", "Python", "FastAPI", "RAG", "Docker", "Linux"],
        features: [
          "Natural language query interface for policy and support requests",
          "RAG pipeline over domain-specific documentation",
          "Automated workflow orchestration via n8n",
          "Human-in-the-loop validation for critical operations",
          "Usage analytics and audit trail"
        ],
        challenges: [
          "Ensuring LLM response accuracy for domain-specific technical queries",
          "Integrating with existing enterprise systems and workflows",
          "Balancing automation with necessary human oversight"
        ],
        impact: "Reduced policy deployment time by 80%, handled 80% of routine support queries automatically, eliminated configuration errors",
        lessons: "RAG with domain-specific documentation dramatically improves LLM accuracy, and human-in-the-loop validation is essential for critical infrastructure operations.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 3,
        title: "Linux Automation Platform",
        tag: "DevOps Automation",
        category: "Infrastructure",
        year: "2025",
        status: "Production",
        overview: "A Flask-based web platform that automates Linux server and Frappe framework management, reducing complex CLI operations to one-click web actions with safety guardrails.",
        problem: "Managing Frappe framework instances across 10+ servers required repetitive manual commands, deep Linux expertise, and was prone to human error.",
        architecture: "Admin → Flask Web UI → Linux Shell → Frappe CLI → 10+ Server Infrastructure",
        solution: "Created a web-based control panel abstracting complex Linux/Frappe operations into one-click actions with confirmation dialogs, logging, and rollback capabilities.",
        technologies: ["Python", "Flask", "Linux", "Frappe", "Shell Scripting", "Git", "Nginx"],
        features: [
          "One-click Frappe instance management across multiple servers",
          "Dev/Production mode switching with environment isolation",
          "Automated backup and restoration with GitHub integration",
          "Site migration and reinstallation tools",
          "Rapid site creation with custom configurations"
        ],
        challenges: [
          "Abstracting destructive CLI operations safely behind a web UI",
          "Supporting multiple Frappe framework versions simultaneously",
          "Ensuring comprehensive logging and rollback for all operations"
        ],
        impact: "10+ servers managed, 50+ sites deployed, 90% reduction in manual operations time",
        lessons: "Web abstraction of CLI operations must include mandatory confirmation, comprehensive logging, and one-click rollback at every step.",
        screenshots: [],
        githubUrl: "https://github.com/ranjitkumar/linux-automation-tool"
      },
      {
        id: 4,
        title: "Proxmox & Frappe Infrastructure Management",
        tag: "Infrastructure Engineering",
        category: "Infrastructure",
        year: "2025",
        status: "Production",
        overview: "Centralized infrastructure management platform integrating Proxmox virtualization APIs with Frappe environment management for unified server operations.",
        problem: "Managing virtualized infrastructure across multiple Proxmox nodes and Frappe servers required separate logins, manual administration, and had no centralized visibility.",
        architecture: "Admin → Control Center → Proxmox API + Frappe CLI → VM/Container Nodes → Storage & Services",
        solution: "Built a unified control plane integrating Proxmox APIs for VM management and Frappe automation for application-layer operations, with centralized monitoring.",
        technologies: ["Python", "Proxmox API", "FastAPI", "Frappe", "Linux", "Docker", "Redis", "Nginx"],
        features: [
          "Unified VM and Frappe instance management dashboard",
          "Automated VM provisioning and Frappe site deployment",
          "Resource monitoring and proactive alerts",
          "Backup and restore automation for both layers",
          "Template-based rapid deployment"
        ],
        challenges: [
          "API rate limiting and connection pooling across Proxmox nodes",
          "State synchronization between virtualization and application layers",
          "Secure credential management across infrastructure"
        ],
        impact: "Centralized management of 5+ Proxmox nodes, 40+ VMs, 12+ Frappe servers with automated provisioning workflows",
        lessons: "Abstraction layers over infrastructure APIs must handle connection pooling, rate limiting, and graceful failover for production reliability.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 5,
        title: "HPC Support Platform",
        tag: "Infrastructure Engineering",
        category: "HPC",
        year: "2025",
        status: "Production",
        overview: "AI-enhanced HPC support platform combining SLURM cluster management with intelligent support systems and user onboarding for high-performance computing environments.",
        problem: "HPC users needed instant technical support and researchers required streamlined access to cluster resources, but support processes were manual and slow.",
        architecture: "Users → HPC Portal → AI Support Agent → SLURM Cluster → GPU/CPU Nodes → Storage",
        solution: "Built a comprehensive HPC platform with AI-powered support, SLURM cluster management, user onboarding workflows, and resource monitoring dashboards.",
        technologies: ["Linux", "HPC", "SLURM", "Ollama", "Python", "FastAPI", "Docker", "GPU"],
        features: [
          "AI-powered support agent for HPC queries",
          "SLURM cluster job management and monitoring",
          "User onboarding and access control workflows",
          "GPU resource allocation and tracking",
          "HPC training and tutorial resources"
        ],
        challenges: [
          "Integrating AI support with existing SLURM workflows",
          "GPU resource allocation across multi-user environments",
          "Building intuitive interfaces for complex HPC operations"
        ],
        impact: "Reduced support response time by 60%, onboarded 100+ users, managed 16-node cluster infrastructure",
        lessons: "Combining AI support systems with traditional HPC tools creates a significantly better user experience for researchers and students.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 6,
        title: "Dataset Generation Engine",
        tag: "AI Engineering",
        category: "AI",
        year: "2025",
        status: "Production",
        overview: "An automated pipeline that generates, validates, and curates high-quality training datasets from source documents using LLMs for fine-tuning domain-specific models.",
        problem: "Fine-tuning LLMs requires high-quality domain-specific datasets that are expensive, time-consuming, and labor-intensive to create manually.",
        architecture: "Source Documents → Processing Pipeline → LLM Generator → Multi-stage Quality Filter → Curated Dataset",
        solution: "Developed an automated multi-stage pipeline using LLMs to generate training samples, with validation checkpoints and quality filters at each stage.",
        technologies: ["Python", "Ollama", "Llama", "FastAPI", "Docker", "RAG", "Linux"],
        features: [
          "Automated dataset generation from source documents",
          "Multi-stage quality filtering and validation",
          "Configurable generation parameters and templates",
          "Multiple output formats (JSON, CSV, Parquet)",
          "Dataset versioning and change tracking"
        ],
        challenges: [
          "Ensuring dataset quality and removing LLM hallucination artifacts",
          "Scalable processing of large document collections",
          "Maintaining consistency across generated samples"
        ],
        impact: "Generated 50K+ training samples with 95% acceptance rate after quality filtering",
        lessons: "Multi-stage generation with validation checkpoints at each stage produces significantly higher quality datasets than single-pass generation approaches.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 7,
        title: "Job Submission Portal",
        tag: "Enterprise Software",
        category: "Enterprise Applications",
        year: "2024",
        status: "Production",
        overview: "A web-based portal streamlining job submission workflows with automated approval routing, status tracking, and integration with backend processing systems.",
        problem: "Job submission and approval processes were manual, paper-based, and lacked tracking capabilities, causing delays and lost submissions.",
        architecture: "Applicants → Web Portal → Approval Workflow → Backend Processing → Notification System",
        solution: "Built a digitized submission portal with automated workflows, role-based approval routing, real-time status tracking, and notification integration.",
        technologies: ["Python", "Django", "MySQL", "JavaScript", "HTML/CSS", "REST API"],
        features: [
          "Online job submission with document upload",
          "Automated approval routing and notifications",
          "Real-time status tracking dashboard",
          "Role-based access for applicants and reviewers",
          "Search and filter capabilities"
        ],
        challenges: [
          "Designing flexible approval workflows for different submission types",
          "Handling document uploads securely at scale",
          "Integrating with existing notification systems"
        ],
        impact: "Streamlined submission process, reduced processing time by 60%, eliminated paper-based workflows",
        lessons: "Flexible workflow engines with configurable routing rules are essential for platforms serving diverse submission types.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 8,
        title: "Canvas Platform Engineering",
        tag: "Infrastructure Engineering",
        category: "Enterprise Applications",
        year: "2025",
        status: "Production",
        overview: "Complete deployment, configuration, and customization of Canvas LMS platform with custom integrations, admin setup, and extended functionality.",
        problem: "Organization needed a fully-functional Learning Management System with custom integrations, but required expert deployment and configuration.",
        architecture: "Users → Canvas LMS → Custom Integrations → Authentication → Database → Storage",
        solution: "Deployed and configured Canvas LMS with custom routes, integrated authentication, Metabase analytics, and extended functionality through custom plugins.",
        technologies: ["Canvas LMS", "Python", "Linux", "Nginx", "Metabase", "MySQL", "Docker"],
        features: [
          "Complete Canvas LMS deployment and configuration",
          "Custom route and domain configuration",
          "Metabase integration for analytics dashboards",
          "Authentication and SSO integration",
          "Custom plugin and widget deployment"
        ],
        challenges: [
          "Configuring Canvas for specific organizational requirements",
          "Integrating analytics without disrupting core LMS functionality",
          "Ensuring scalability for growing user base"
        ],
        impact: "Successfully deployed enterprise LMS platform with analytics, serving organizational learning and development needs",
        lessons: "Platform engineering for enterprise applications requires deep understanding of both the application architecture and the underlying infrastructure.",
        screenshots: [],
        githubUrl: null
      }
    ],
    archive: [
      {
        id: 9,
        title: "SMS Middleware Platform",
        tag: "System Integration",
        category: "Backend",
        year: "2025",
        status: "Production",
        overview: "A unified SMS middleware with provider abstraction, centralized logging, and automatic failover between Twilio and Aisensy gateways.",
        problem: "Multiple applications needed SMS capabilities but direct provider integration created duplication, inconsistency, and single-provider dependency.",
        architecture: "Applications → FastAPI Gateway → Provider Adapter → Twilio/Aisensy → Mobile Networks",
        solution: "Built a unified SMS middleware with provider abstraction layer, centralized logging, delivery analytics, and automatic failover between providers.",
        technologies: ["Python", "FastAPI", "Django", "MySQL", "Twilio", "Aisensy"],
        features: [
          "Multi-provider SMS gateway with automatic failover",
          "Centralized message logging and delivery tracking",
          "Bulk SMS with scheduling capabilities",
          "Delivery analytics and reporting dashboard",
          "REST API for application integration"
        ],
        challenges: ["Provider API rate limits and failover coordination", "Message delivery guarantees across providers", "Scalability for peak load handling"],
        impact: "100K+ API requests processed, 99.9% delivery rate, 3+ enterprise applications integrated",
        lessons: "Provider abstraction with circuit breaker pattern ensures reliability even when individual providers experience failures.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 10,
        title: "AI-Powered Resume Parser",
        tag: "Generative AI",
        category: "AI",
        year: "2025",
        status: "Production",
        overview: "An intelligent resume parsing system powered by LLaMA 3.3 that extracts, summarizes, and structures candidate information from uploaded resumes.",
        problem: "Manual resume screening was time-consuming and inconsistent, with recruiters spending hours extracting key information from diverse resume formats.",
        architecture: "Users → Django App → LLaMA 3.3 → Text Extraction Engine → Structured Output",
        solution: "Built a web-based resume parser using LLaMA 3.3 for NLP-powered extraction and summarization, supporting multiple document formats.",
        technologies: ["Python", "Django", "LLaMA 3.3", "Ollama", "REST API", "PDF Processing"],
        features: [
          "Multi-format resume upload (PDF, DOCX, etc.)",
          "AI-powered professional summary generation",
          "Skills and experience extraction",
          "REST API for external integration",
          "Web dashboard for result visualization"
        ],
        challenges: ["Handling diverse resume formats and layouts", "Optimizing LLM inference latency", "Ensuring consistent extraction accuracy"],
        impact: "500+ resumes processed with 92% extraction accuracy, reducing screening time by 70%",
        lessons: "LLM-based extraction with format-specific preprocessing significantly improves accuracy over traditional NLP approaches.",
        screenshots: [],
        githubUrl: "https://github.com/ranjitkumar/ai-resume-parser"
      },
      {
        id: 11,
        title: "SSL Lifecycle Management Portal",
        tag: "Infrastructure Engineering",
        category: "Infrastructure",
        year: "2025",
        status: "Production",
        overview: "Centralized SSL certificate management platform with automatic discovery, Let's Encrypt renewal, and multi-server deployment across 30+ certificates.",
        problem: "SSL certificates across 12+ servers required manual renewal tracking and installation, leading to expiry incidents and security gaps.",
        architecture: "Portal → Certificate Discovery → Let's Encrypt API → Auto-Renewal → Multi-server Deployment → Monitoring",
        solution: "Built a centralized SSL platform with automatic certificate discovery, renewal, deployment, and pre-expiry alerting across all managed servers.",
        technologies: ["Python", "Flask", "Let's Encrypt", "Nginx", "Linux", "Docker", "Certbot"],
        features: [
          "Automatic certificate discovery across servers",
          "Let's Encrypt auto-renewal with validation",
          "Multi-server certificate deployment",
          "Pre-expiry monitoring and alerts",
          "Certificate inventory dashboard"
        ],
        challenges: ["Discovering certificates across distributed servers", "Zero-downtime certificate rotation", "Diverse web server configuration handling"],
        impact: "Zero SSL expiry incidents, automated renewal for 30+ certificates, centralized certificate visibility",
        lessons: "Certificate lifecycle automation must include pre-expiry alerting and automated post-renewal testing for true production reliability.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 12,
        title: "AI/ML Projects & Model Deployment",
        tag: "AI Engineering",
        category: "AI",
        year: "2025",
        status: "Active",
        overview: "Initiated and deployed multiple AI solutions including self-hosted LLM servers, model testing infrastructure, and AI-powered data extraction systems.",
        problem: "Organization needed on-premise AI capabilities but lacked infrastructure and expertise for deploying and testing LLM models locally.",
        architecture: "Source Data → Model Selection → Ollama Deployment → API Serving → Application Integration",
        solution: "Deployed self-hosted AI infrastructure using Ollama, tested multiple models (LLaMA, H2O GPT), and built AI-powered data extraction systems.",
        technologies: ["Python", "LLaMA", "Ollama", "H2O GPT", "FastAPI", "Linux", "GPU"],
        features: [
          "Self-hosted LLM inference server deployment",
          "Multi-model testing and evaluation framework",
          "AI-powered data extraction from documents",
          "REST API for model serving",
          "GPU resource management"
        ],
        challenges: ["GPU memory optimization for multiple models", "Model selection for specific use cases", "Infrastructure scaling for AI workloads"],
        impact: "Deployed 3+ AI models, established self-hosted AI infrastructure, enabled AI capabilities for multiple internal applications",
        lessons: "Self-hosted AI infrastructure provides data privacy and cost benefits but requires careful GPU resource planning and model selection.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 13,
        title: "Frappe Server Setup & Security",
        tag: "Infrastructure Engineering",
        category: "Infrastructure",
        year: "2025",
        status: "Production",
        overview: "Provisioned, secured, and maintained 12+ Frappe servers with advanced authentication (OTP, JWT, SSO), SSL automation, and security hardening.",
        problem: "Multiple Frappe-based applications needed secure, production-grade server infrastructure with consistent security policies and authentication systems.",
        architecture: "Users → HAProxy → Frappe Servers → JWT/OTP Auth → Application Layer → Database",
        solution: "Implemented comprehensive server security including OTP-based login, JWT authentication, SSO integration, automated SSL, and Fail2Ban protection.",
        technologies: ["Frappe", "Linux", "Nginx", "Let's Encrypt", "Fail2Ban", "JWT", "OTP", "SSO"],
        features: [
          "OTP-based login system for enhanced security",
          "JWT-based authentication for secure sessions",
          "Single Sign-On (SSO) for cross-application access",
          "Automated SSL setup with Let's Encrypt",
          "Fail2Ban configuration for intrusion prevention"
        ],
        challenges: ["Consistent security across 12+ servers", "Authentication integration without disrupting existing workflows", "Balancing security with user convenience"],
        impact: "12+ production servers secured, zero security incidents, consistent authentication across all applications",
        lessons: "Defense-in-depth security approach with multiple authentication layers provides enterprise-grade protection for production deployments.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 14,
        title: "API Integration & Middleware Enhancements",
        tag: "System Integration",
        category: "Backend",
        year: "2025",
        status: "Production",
        overview: "Integrated enterprise APIs including WhatsApp, SMS, and payment gateways with enhanced token management, monitoring, and reliability patterns.",
        problem: "Enterprise applications needed reliable integration with external communication and payment APIs with consistent error handling and monitoring.",
        architecture: "Applications → Middleware Layer → API Gateway → External Providers (WhatsApp/SMS/Payments)",
        solution: "Enhanced middleware with Aisensy WhatsApp, Twilio SMS, and Razorpay payment integrations, plus improved token management and monitoring.",
        technologies: ["Python", "FastAPI", "Aisensy", "Twilio", "Razorpay", "MySQL"],
        features: [
          "WhatsApp Business API integration via Aisensy",
          "Twilio SMS gateway for transactional messages",
          "Razorpay payment processing integration",
          "Centralized token management and rotation",
          "Middleware logging and monitoring dashboards"
        ],
        challenges: ["Managing API rate limits across different providers", "Ensuring message delivery reliability", "Payment transaction security and consistency"],
        impact: "Integrated 3 major external APIs, improved middleware reliability by 40%, enabled real-time communication workflows",
        lessons: "Centralized middleware with consistent error handling, retry logic, and monitoring is essential for multi-provider API integration.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 15,
        title: "Database Management & Query Optimization",
        tag: "Database Engineering",
        category: "Backend",
        year: "2025",
        status: "Production",
        overview: "Optimized MySQL/MariaDB database queries, designed synchronization workflows, and implemented custom evaluation logic for improved system performance.",
        problem: "Complex database queries in evaluation and reporting systems were slow, affecting user experience and report generation times.",
        architecture: "Application → Query Optimization → MySQL/MariaDB → Indexed Tables → Optimized Reporting",
        solution: "Fine-tuned complex queries, designed data synchronization between systems, implemented custom-grade evaluation logic, and optimized database indexes.",
        technologies: ["MySQL", "MariaDB", "Python", "Django", "SQL", "Query Optimization"],
        features: [
          "Complex AUMS query optimization for better performance",
          "Data synchronization between multiple systems",
          "Custom-grade evaluation logic implementation",
          "Database indexing and schema optimization",
          "Reporting query performance improvements"
        ],
        challenges: ["Balancing query performance with data consistency", "Synchronizing data across heterogeneous systems", "Optimizing for both OLTP and reporting workloads"],
        impact: "Reduced report generation time by 60%, improved query response times by 40%, enabled real-time dashboards",
        lessons: "Database optimization requires understanding both the query patterns and the business logic to make effective indexing and schema decisions.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 16,
        title: "HPC & Cluster Infrastructure Management",
        tag: "Infrastructure Engineering",
        category: "HPC",
        year: "2025",
        status: "Production",
        overview: "Implemented and managed HPC solutions with SLURM, Docker Swarm, and Dask clusters for data-intensive workloads and parallel processing.",
        problem: "Research and data processing workloads required high-performance computing infrastructure that was not available through traditional server setups.",
        architecture: "Users → Job Scheduler → SLURM/Docker Swarm → Compute Nodes → GPU/CPU → Storage",
        solution: "Implemented HPC solutions including SLURM cluster deployment, Docker Swarm configuration, Dask distributed computing, and multi-GPU JupyterHub.",
        technologies: ["Linux", "HPC", "SLURM", "Docker Swarm", "Dask", "JupyterHub", "GPU"],
        features: [
          "SLURM cluster deployment and management",
          "Docker Swarm for parallel task execution",
          "Dask distributed computing setup",
          "Multi-GPU JupyterHub for AI training",
          "User management and access control"
        ],
        challenges: ["GPU resource allocation across containers", "Network configuration for cluster communication", "User onboarding and training for HPC systems"],
        impact: "Managed 16-node cluster infrastructure, enabled parallel processing for research workloads, trained 50+ HPC users",
        lessons: "HPC infrastructure requires careful planning of network topology, resource allocation policies, and comprehensive user documentation for adoption.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 17,
        title: "Selenium Automation Tool",
        tag: "Automation",
        category: "Automation",
        year: "2024",
        status: "Production",
        overview: "An automation tool built with Selenium for automating repetitive web tasks, data extraction, and browser-based workflow automation.",
        problem: "Repetitive web-based tasks and data extraction processes were consuming significant manual effort across multiple teams.",
        architecture: "User Configuration → Selenium Script → WebDriver → Target Websites → Extracted Data",
        solution: "Built a Selenium-based automation framework for form submissions, web data extraction, and browser compatibility testing.",
        technologies: ["Python", "Selenium", "WebDriver", "HTML", "CSS"],
        features: [
          "Automated form submissions and web interactions",
          "Web data extraction and scraping pipelines",
          "Browser compatibility and regression testing",
          "Task scheduling and execution automation",
          "Configurable workflow definitions"
        ],
        challenges: ["Handling dynamic web content and JavaScript-rendered pages", "Maintaining scripts across website updates", "Scaling automation across multiple browsers"],
        impact: "Automated 10+ repetitive workflows, saving 20+ hours per week across teams",
        lessons: "Robust selector strategies and regular maintenance cycles are essential for production web automation reliability.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 18,
        title: "Web Applications for Multiple API Services",
        tag: "Full Stack",
        category: "Enterprise Applications",
        year: "2024",
        status: "Production",
        overview: "A suite of internal web tools integrating AI-powered question generation, student gatepass management, and centralized API logging with Telegram notifications.",
        problem: "Multiple campus workflows needed digitization with AI capabilities, but required separate applications with consistent integration patterns.",
        architecture: "Users → Django/FastAPI Apps → AI/ML Models → APIs → Telegram Notifications → Databases",
        solution: "Built a suite of integrated web applications including AI question paper generator, student gatepass system, and centralized API logging.",
        technologies: ["Python", "Django", "FastAPI", "AI/ML Models", "JavaScript", "Telegram API"],
        features: [
          "AI-based question paper generator trained on institutional data",
          "Student gatepass in/out system with faculty approval workflow",
          "Centralized API request logging with Telegram notifications",
          "Role-based access across all applications",
          "Integrated notification and alerting"
        ],
        challenges: ["Training AI models on limited institutional datasets", "Real-time approval workflows with notification delivery", "Consistent UX across multiple applications"],
        impact: "Digitized 3 campus workflows, reduced manual processing by 50%, enabled AI-powered content generation",
        lessons: "Building integrated application suites requires consistent API patterns, shared authentication, and unified monitoring for operational efficiency.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 19,
        title: "Cluster Computing & Parallel Processing",
        tag: "Infrastructure Engineering",
        category: "HPC",
        year: "2024",
        status: "Production",
        overview: "Configured Docker Swarm and Dask clusters with multi-GPU JupyterHub for parallel processing, AI model training, and large-scale data workloads.",
        problem: "Data-intensive AI training and processing workloads required parallel computing infrastructure that single servers could not provide.",
        architecture: "Users → JupyterHub → Docker Swarm/Dask → Multi-GPU Cluster → Distributed Storage",
        solution: "Set up Docker Swarm and Dask clusters with cluster/office workload modes and integrated JupyterHub with multiple GPU cards.",
        technologies: ["Docker Swarm", "Dask", "Python", "JupyterHub", "Linux", "GPU"],
        features: [
          "Docker Swarm cluster for parallel task execution",
          "Dask distributed computing for data processing",
          "Multi-GPU JupyterHub for AI model training",
          "Cluster and Office workload optimization modes",
          "Distributed storage integration"
        ],
        challenges: ["GPU resource allocation across containers and users", "Network latency optimization for cluster communication", "Workload distribution balancing"],
        impact: "Enabled parallel AI training, 40% faster data processing, supported multi-user research environment",
        lessons: "Hybrid cluster architectures with dedicated GPU nodes and flexible workload modes provide optimal resource utilization for diverse computing needs.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 20,
        title: "Custom Application Deployment & Testing",
        tag: "DevOps",
        category: "Infrastructure",
        year: "2025",
        status: "Production",
        overview: "Deployed and configured multiple enterprise applications including Canvas LMS, Metabase analytics, and custom Frappe applications across production servers.",
        problem: "Enterprise applications required expert deployment, configuration, and integration with existing infrastructure and authentication systems.",
        architecture: "Requirements → Server Provisioning → Application Deployment → Configuration → Integration → Testing",
        solution: "Executed end-to-end deployment of Canvas LMS, Metabase, Gameplan, and custom Frappe apps with full configuration and testing.",
        technologies: ["Frappe", "Canvas LMS", "Metabase", "Python", "Linux", "Nginx"],
        features: [
          "Complete Canvas LMS setup with custom routes",
          "Metabase analytics platform integration",
          "Gameplan application deployment on Frappe",
          "Custom widget and plugin installation",
          "Integration testing and validation"
        ],
        challenges: ["Application-specific configuration requirements", "Integration with existing authentication and infrastructure", "Testing across heterogeneous environments"],
        impact: "Successfully deployed 4+ enterprise applications with zero production incidents",
        lessons: "Systematic deployment workflows with documented configuration steps and validation testing ensure consistent enterprise application deployments.",
        screenshots: [],
        githubUrl: null
      },
      {
        id: 21,
        title: "Phoenix Rebars Website",
        tag: "Web Development",
        category: "Enterprise Applications",
        year: "2023",
        status: "Production",
        overview: "A professional business website for a construction company showcasing services, completed projects, and providing client inquiry capabilities.",
        problem: "Construction company needed a professional online presence to showcase their portfolio and generate client leads.",
        architecture: "Visitors → Static Website → Contact Forms → Business Inquiries",
        solution: "Built a modern, responsive website highlighting company services, completed projects, and contact information with lead generation forms.",
        technologies: ["HTML", "CSS", "JavaScript", "Responsive Design", "Web Hosting"],
        features: [
          "Modern and responsive business website design",
          "Dedicated services and projects showcase pages",
          "Contact form for client inquiries",
          "Project portfolio with image gallery",
          "SEO-optimized content structure"
        ],
        challenges: ["Creating compelling visual design for construction portfolio", "Ensuring fast load times with image-heavy content", "Mobile responsiveness for on-site access"],
        impact: "Established online presence, generated client inquiries through website, showcased 20+ completed projects",
        lessons: "Even simple static websites require careful planning of information architecture, visual hierarchy, and mobile user experience.",
        screenshots: [],
        githubUrl: null
      }
    ]
  },

  timeline: [
    {
      period: "2022 – 2024",
      role: "System Engineer",
      description: "Started my professional journey managing Linux systems, servers, enterprise applications, infrastructure deployments, troubleshooting, production support, and platform operations.",
      highlights: [
        "Linux Administration",
        "Server Management",
        "Infrastructure Support",
        "Platform Deployment",
        "Production Troubleshooting",
        "Application Operations"
      ],
      icon: "monitor"
    },
    {
      period: "2024 – Present",
      role: "Application Engineer | Automation & AI",
      description: "Expanded responsibilities beyond infrastructure into automation, backend engineering, AI systems, enterprise integrations, HPC environments, workflow orchestration, and intelligent platform development.",
      highlights: [
        "AI Workflow Automation",
        "Enterprise Application Development",
        "Infrastructure Automation",
        "HPC & Cluster Computing",
        "Platform Engineering",
        "API Development",
        "Self-hosted AI Systems",
        "Workflow Orchestration"
      ],
      icon: "sparkles",
      current: true
    }
  ],

  currentPosition: {
    role: "Application Engineer | Automation & AI",
    focus: [
      "AI Agents",
      "Workflow Automation",
      "Platform Engineering",
      "HPC Automation",
      "Self-hosted AI",
      "Infrastructure Intelligence"
    ]
  },

  currentlyBuilding: [
    { name: "AI Agent Workflows", description: "Building autonomous AI agents for complex enterprise workflow automation", icon: "bot", status: "In Progress" },
    { name: "Voice Automation Systems", description: "Voice-controlled infrastructure and workflow management", icon: "mic", status: "Experimental" },
    { name: "HPC Automation", description: "Automating HPC cluster operations and job scheduling", icon: "cpu", status: "In Progress" },
    { name: "Job Submission Portal", description: "Streamlining job and application submission workflows", icon: "globe", status: "Production" },
    { name: "Dataset Generation Engine", description: "Automated LLM fine-tuning dataset creation pipeline", icon: "database", status: "Production" },
    { name: "Network Automation Platform", description: "AI-assisted network policy and configuration management", icon: "zap", status: "In Progress" },
    { name: "Self-hosted AI Infrastructure", description: "Private, secure AI serving infrastructure for enterprise", icon: "server", status: "Production" }
  ],

  architectureDiagrams: [
    {
      title: "Enterprise AI Platform",
      description: "End-to-end AI platform architecture with agent orchestration",
      nodes: [
        { id: "users", label: "Users", icon: "user" },
        { id: "frontend", label: "Frontend", icon: "layout" },
        { id: "gateway", label: "API Gateway", icon: "shuffle" },
        { id: "services", label: "FastAPI Services", icon: "code" },
        { id: "agents", label: "AI Agents", icon: "brain" },
        { id: "ollama", label: "Ollama", icon: "cpu" },
        { id: "db", label: "Databases", icon: "database" }
      ]
    },
    {
      title: "Infrastructure Platform",
      description: "High-availability production infrastructure stack",
      nodes: [
        { id: "users", label: "Users", icon: "user" },
        { id: "haproxy", label: "HAProxy", icon: "shuffle" },
        { id: "app", label: "Application Servers", icon: "server" },
        { id: "redis", label: "Redis", icon: "zap" },
        { id: "db", label: "Database Layer", icon: "database" },
        { id: "backup", label: "Backup Layer", icon: "hard-drive" }
      ]
    },
    {
      title: "HPC Environment",
      description: "High-performance computing cluster architecture",
      nodes: [
        { id: "users", label: "Users", icon: "user" },
        { id: "portal", label: "Portal", icon: "layout" },
        { id: "slurm", label: "SLURM", icon: "cpu" },
        { id: "nodes", label: "Cluster Nodes", icon: "server" },
        { id: "gpu", label: "GPU Resources", icon: "cpu" },
        { id: "storage", label: "Storage", icon: "database" }
      ]
    }
  ],

  currentFocus: {
    title: "Building the Next Generation of AI Infrastructure",
    items: [
      { name: "Agentic AI Systems", description: "Building autonomous AI agents for complex task execution", icon: "bot" },
      { name: "Voice-based Automation", description: "Voice-controlled infrastructure and workflow automation", icon: "mic" },
      { name: "Self-hosted AI Platforms", description: "Private, secure AI infrastructure for enterprise use", icon: "server" },
      { name: "Infrastructure Automation", description: "Automating everything from provisioning to monitoring", icon: "zap" },
      { name: "AI Operations (AIOps)", description: "Using AI for intelligent system operations and observability", icon: "activity" },
      { name: "Workflow Intelligence", description: "Smart workflows that adapt and optimize automatically", icon: "git-branch" },
      { name: "Distributed AI Systems", description: "Scalable AI architectures across distributed infrastructure", icon: "globe" },
      { name: "Open Source AI", description: "Contributing to and building with open-source AI tools", icon: "github" }
    ]
  },

  mission: "I believe the future belongs to engineers who can combine Artificial Intelligence, Automation, Infrastructure, and Software Engineering to create systems that amplify human capability. My goal is to build intelligent platforms that eliminate repetitive work, accelerate decision-making, and unlock new possibilities through AI."
};
